import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import Navbar from "../components/Navbar";
import {
    FaArrowLeft,
    FaSearch,
    FaBarcode,
    FaCalendarAlt,
    FaUser,
    FaBoxOpen,
    FaCheckCircle,
    FaPaperPlane
} from "react-icons/fa";

function UserScanDetails() {

    const { username } = useParams();
    const navigate = useNavigate();

    const today = new Date().toISOString().split("T")[0];
    const firstOfMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
    ).toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState(firstOfMonth);
    const [toDate, setToDate] = useState(today);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [marking, setMarking] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // ─── Load scans ────────────────────────────────────────────────
    const loadScans = async () => {

        setLoading(true);
        setError(null);
        setSuccessMsg(null);

        try {

            const res = await api.get("/barcodes/user-scans", {
                params: { username, fromDate, toDate }
            });

            setData(res.data.data);
            setTotal(res.data.total);
            setSearched(true);

        } catch (err) {

            setError(
                err.response?.data?.message || "Unable to load scan details"
            );

        } finally {

            setLoading(false);

        }

    };

    // ─── Mark items as sent ─────────────────────────────────────────
    const pendingCount = data.filter((r) => !r.items_sent_at).length;

    const handleMarkSent = async () => {

        if (pendingCount === 0) {
            setError("All records in this range are already marked as sent.");
            return;
        }

        const confirmed = window.confirm(
            `Mark ${pendingCount} coupon item(s) as SENT for "${username}" (${fromDate} → ${toDate})?\n\nThis will record today as the delivery date and cannot be undone.`
        );

        if (!confirmed) return;

        setMarking(true);
        setError(null);
        setSuccessMsg(null);

        try {

            const res = await api.post("/barcodes/mark-items-sent", {
                username,
                fromDate,
                toDate
            });

            setSuccessMsg(res.data.message || "Items marked as sent successfully.");

            // Reload to show updated items_sent_at values
            await loadScans();

        } catch (err) {

            setError(
                err.response?.data?.message || "Failed to mark items as sent."
            );

        } finally {

            setMarking(false);

        }

    };

    // ─── Helpers ────────────────────────────────────────────────────
    const completedCount = data.filter((r) => r.items_sent_at).length;

    return (
        <>
            <Navbar />

            <div className="container mt-4 pb-5">

                {/* ── Page Header ── */}
                <div className="d-flex align-items-center gap-3 mb-4">
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate("/shops-list")}
                    >
                        <FaArrowLeft className="me-1" />
                        Back
                    </button>
                    <div>
                        <h4 className="mb-0 d-flex align-items-center gap-2">
                            <FaUser className="text-primary" />
                            Scan Report —{" "}
                            <span className="text-primary fw-bold">{username}</span>
                        </h4>
                        <small className="text-muted">
                            View barcodes scanned by this user within a date range
                        </small>
                    </div>
                </div>

                {/* ── Filter Card ── */}
                <div
                    className="card shadow-sm border-0 mb-4"
                    style={{ borderRadius: "12px" }}
                >
                    <div className="card-body">
                        <div className="row g-3 align-items-end">

                            <div className="col-md-4">
                                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                                    <FaCalendarAlt className="text-primary" />
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                                    <FaCalendarAlt className="text-primary" />
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>

                            <div className="col-md-4">
                                <button
                                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                    onClick={loadScans}
                                    disabled={loading || marking}
                                    style={{ height: "38px" }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <FaSearch />
                                            Search
                                        </>
                                    )}
                                </button>
                            </div>

                        </div>
                    </div>
                </div>

                {/* ── Alerts ── */}
                {error && (
                    <div className="alert alert-danger alert-dismissible">
                        {error}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setError(null)}
                        />
                    </div>
                )}

                {successMsg && (
                    <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2">
                        <FaCheckCircle />
                        {successMsg}
                        <button
                            type="button"
                            className="btn-close ms-auto"
                            onClick={() => setSuccessMsg(null)}
                        />
                    </div>
                )}

                {/* ── Results Card ── */}
                {searched && (
                    <div
                        className="card shadow-sm border-0"
                        style={{ borderRadius: "12px" }}
                    >
                        {/* Card Header */}
                        <div
                            className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2"
                            style={{
                                background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                                color: "#fff",
                                borderRadius: "12px 12px 0 0",
                                padding: "12px 20px"
                            }}
                        >
                            {/* Left: title + counts */}
                            <div className="d-flex align-items-center gap-3">
                                <h6 className="mb-0 d-flex align-items-center gap-2">
                                    <FaBarcode />
                                    Scanned Barcodes
                                </h6>
                                <span className="badge bg-light text-primary fw-bold">
                                    {total} total
                                </span>
                                {completedCount > 0 && (
                                    <span className="badge bg-success fw-bold">
                                        <FaCheckCircle className="me-1" />
                                        {completedCount} sent
                                    </span>
                                )}
                                {pendingCount > 0 && (
                                    <span className="badge bg-warning text-dark fw-bold">
                                        {pendingCount} pending
                                    </span>
                                )}
                            </div>

                            {/* Right: Mark as Sent button */}
                            <button
                                className="btn btn-light btn-sm d-flex align-items-center gap-2 fw-semibold"
                                onClick={handleMarkSent}
                                disabled={marking || loading || pendingCount === 0}
                                style={{ color: "#1d4ed8", minWidth: "180px" }}
                            >
                                {marking ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <FaPaperPlane />
                                        Mark Items as Sent
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Card Body */}
                        <div className="card-body p-0">
                            {data.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <FaBoxOpen size={40} className="mb-2 opacity-50" />
                                    <p className="mb-0">
                                        No barcodes scanned in this date range.
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>#</th>
                                                <th>Item Name</th>
                                                <th>Barcode</th>
                                                <th>Scanned At</th>
                                                <th>Status</th>
                                                <th>Items Sent At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((row, index) => {

                                                const isCompleted = Boolean(row.items_sent_at);

                                                return (
                                                    <tr
                                                        key={row.id}
                                                        style={
                                                            isCompleted
                                                                ? { background: "#f0fdf4" }
                                                                : {}
                                                        }
                                                    >
                                                        {/* # */}
                                                        <td className="text-muted align-middle">
                                                            {index + 1}
                                                        </td>

                                                        {/* Item Name */}
                                                        <td className="align-middle">
                                                            <span className="fw-semibold">
                                                                {row.item_name}
                                                            </span>
                                                        </td>

                                                        {/* Barcode */}
                                                        <td className="align-middle">
                                                            <span
                                                                className="badge bg-secondary font-monospace"
                                                                style={{
                                                                    fontSize: "12px",
                                                                    letterSpacing: "0.5px"
                                                                }}
                                                            >
                                                                {row.barcode_value}
                                                            </span>
                                                        </td>

                                                        {/* Scanned At */}
                                                        <td
                                                            className="text-muted align-middle"
                                                            style={{ whiteSpace: "nowrap" }}
                                                        >
                                                            {new Date(
                                                                row.scanned_at
                                                            ).toLocaleString()}
                                                        </td>

                                                        {/* Status badge */}
                                                        <td className="align-middle">
                                                            {isCompleted ? (
                                                                <span className="badge bg-success d-inline-flex align-items-center gap-1">
                                                                    <FaCheckCircle />
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-warning text-dark">
                                                                    Pending
                                                                </span>
                                                            )}
                                                        </td>

                                                        {/* Items Sent At */}
                                                        <td
                                                            className="align-middle"
                                                            style={{ whiteSpace: "nowrap" }}
                                                        >
                                                            {isCompleted ? (
                                                                <span className="text-success fw-semibold">
                                                                    {new Date(
                                                                        row.items_sent_at
                                                                    ).toLocaleString()}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>

                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                    </div>
                )}

            </div>
        </>
    );
}

export default UserScanDetails;
